import React, { KeyboardEvent, useContext } from 'react';
import {
  Editor,
  EditorState,
  Modifier,
  ContentBlock,
  ContentState,
  CompositeDecorator,
  SelectionState,
  DraftEditorCommand,
  DraftHandleValue,
  EntityInstance,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { Row, Col, Card, Spin, ConfigProvider, theme as antdTheme } from 'antd';
import { WordInfo } from './models';
import Word from './components/Word';
import './DraftEditor.css';
import ErrorMessage from './components/ErrorMessage';
import { betweenOrEqual } from './utils';
import { StreamingSpeechHandler, makeWAVHeader } from './streamingSpeech';
import { StreamingRecognizeResponse } from '@gen/proto/lr/speech/v2beta1/speech_pb';
import {
  CreateTranscriptRequest,
  CreateTranscriptResponse,
  GetTranscriptRequest,
  InitUploadRequest,
  InitUploadResponse,
  ListTranscriptsRequest,
  Transcript,
  TranscriptMetadata,
  UpdateTranscriptRequest,
} from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import { TranscriptServiceClient, ServiceError } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb_service';
import { durationToFloat, floatToDuration, isTransientGrpcError } from './helpers';
import { LanguageCodeOption } from './speech';
import RecordingControls from './components/RecordingControls';
import AudioPlayer from './components/AudioPlayer';
import { grpc } from '@improbable-eng/grpc-web';
import KeyboardUtils from './keyboard-utils';
import debounce from 'lodash/debounce';
import axios from 'axios';
import { runSegmentPostProcess } from './dictate/postprocess';

enum EntityType {
  SYNC_WORD = 'SYNC_WORD',
  SYNC_SEGMENT = 'SYNC_SEGMENT',
}

type CurrentTimeT = number;
const CurrentTimeContext = React.createContext<CurrentTimeT>(0.0);

type SyncWordProps = {
  getCurrentTime: () => number;
  playerRef: React.RefObject<HTMLAudioElement>;
} & any;

const SyncWord = (props: SyncWordProps) => {
  const currentTime = useContext(CurrentTimeContext);

  const entity = props.contentState.getEntity(props.entityKey);
  const entityData: WordInfo = entity.getData();
  const active = betweenOrEqual(entityData.startTime, currentTime, entityData.endTime);

  const maybeSetTime = (time: number) => {
    if (props.playerRef.current === null) return;
    props.playerRef.current.currentTime = time;
  };

  return (
    <Word active={active} wordInfo={entityData} onClick={(wordInfo: WordInfo) => maybeSetTime(wordInfo.startTime)}>
      {props.children}
    </Word>
  );
};

const syncWordStrategy = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState,
) => {
  contentBlock.findEntityRanges((char) => {
    const entityKey = char.getEntity();
    if (entityKey === null) return false;

    const entity = contentState.getEntity(entityKey);
    if (entity.getType() !== EntityType.SYNC_WORD) {
      return false;
    }

    return true;
  }, callback);
};

const defaultLanguageCode = 'is-IS';

const defaultRecVolume = () => {
  const defaultValue: number = 100;
  let recVolumeVal: string | null = null;
  try {
    recVolumeVal = localStorage.getItem('tiro:dictate:recvolume');
  } catch (e) {
    return defaultValue;
  }

  if (recVolumeVal === null) {
    return defaultValue;
  }

  return Number(recVolumeVal);
};

const defaultMetadata = (): TranscriptMetadata.AsObject => {
  return {
    fileType: TranscriptMetadata.FileType.AUDIO,
    dictation: true,
    languageCode: defaultLanguageCode,
    description: '',
    subject: '',
    originalUri: '',
    originalFilename: '',
    additionalMetadataMap: [],
    keywordsList: [],
    speakersMap: [],
    waveformUri: '',
    originalCharLength: 0,
    originalByteLength: 0,
    dataSource: TranscriptMetadata.DataSource.FILE_UPLOAD,
  };
};

const setDefaultRecVolume = (value: number) => {
  try {
    localStorage.setItem('tiro:dictate:recvolume', value.toString());
  } catch (e) {
    console.warn('Could not persist recording volume to localStorage:', e);
  }
};

const defaultDarkMode = (): boolean => {
  try {
    return localStorage.getItem('tiro:dictate:darkmode') === 'true';
  } catch (e) {
    return false;
  }
};

const setDefaultDarkMode = (value: boolean) => {
  try {
    localStorage.setItem('tiro:dictate:darkmode', value.toString());
  } catch (e) {
    console.warn('Could not persist dark mode to localStorage:', e);
  }
};

type EntityObject = {
  entityKey: string;
  blockKey: string;
  entity: EntityInstance;
  start: number;
  end: number;
};
// Thanks Farbod: https://stackoverflow.com/a/46595796
const getEntities = (content: ContentState, entityType?: string): EntityObject[] => {
  const entities: EntityObject[] = [];
  content.getBlocksAsArray().forEach((block) => {
    let selectedEntity: { entityKey: string; blockKey: string; entity: EntityInstance };
    block.findEntityRanges(
      (character) => {
        if (character.getEntity() !== null) {
          const entity = content.getEntity(character.getEntity());
          if (!entityType || (entityType && entity.getType() === entityType)) {
            selectedEntity = {
              entityKey: character.getEntity(),
              blockKey: block.getKey(),
              entity: content.getEntity(character.getEntity()),
            };
            return true;
          }
        }
        return false;
      },
      (start, end) => {
        entities.push({ ...selectedEntity, start, end });
      },
    );
  });
  return entities;
};

const convertToTranscriptSegmentProto = (contentState: ContentState): Transcript.TranscriptSegment => {
  const entities = getEntities(contentState);
  const segmentPb = new Transcript.TranscriptSegment();

  const block = contentState.getFirstBlock();
  const text = block.getText();

  entities.forEach((entityObj: EntityObject) => {
    const { start, end, entity } = entityObj;
    const wordInfo = new Transcript.TranscriptSegment.WordInfo();
    const { startTime, endTime } = entity.getData() as WordInfo;
    wordInfo.setWord(text.slice(start, end));
    wordInfo.setStartTime(floatToDuration(startTime));
    wordInfo.setEndTime(floatToDuration(endTime));
    segmentPb.addWords(wordInfo);
  });

  return segmentPb;
};

type TranscriptEditorCommand =
  | ('transcript-editor-move-back' | 'transcript-editor-move-forward' | 'transcript-editor-pause-or-play')
  | DraftEditorCommand;

// TODO(rkjaran): make sure the whole thing doesn't rerender on an accessToken change
export type DictationEditorProps = {
  initialSubject?: string;
  initialKeywords?: string[];
  languageCode?: string;
  restricted?: boolean;
  transcriptServerUrl: string;
  accessToken?: string;
  signOut?: () => void;
  speechServerUrl: string;
  speechServerKey: string;
  onPostSave?: (transcriptName?: string) => void;
};

interface DictationEditorState {
  editorState: EditorState;

  /**
   * This is true if the editor is waiting for something to be loaded or
   * processed.
   */
  waiting: boolean;

  /**
   * Time position in audio file, in seconds.
   */
  currentTime: number;

  duration: number;

  /**
   * ASR decoding progress in percentage. Set to 100% if completed or on
   * decoding error.
   */
  progressPercent: number;

  /**
   * Set if there is an error. User-facing error message.
   */
  error?: string;

  currentHypothesis: string;

  recording: boolean;
  recordingStarted: boolean;
  recordingStopped: boolean;
  saved: boolean;
  saving: boolean;
  languageCode: LanguageCodeOption['value'];

  editable: boolean;

  metadata: TranscriptMetadata.AsObject;
  originalTranscript: Transcript;

  /**
   * Are we updating/editing/reviewing a previously recorded dictation?
   */
  transcriptName?: string;

  uploadInProgress: boolean;
  currentRecVolume: number;

  darkMode: boolean;
}

const initialDictationEditorState = {
  waiting: false,
  currentTime: 0,
  duration: 0,
  progressPercent: 0,
  currentHypothesis: '',
  recording: false,
  recordingStarted: false,
  recordingStopped: false,
  saved: false,
  saving: false,
  languageCode: defaultLanguageCode,
  editable: true,
  metadata: defaultMetadata(),
  originalTranscript: new Transcript(),
  uploadInProgress: false,
  currentRecVolume: defaultRecVolume(),
  darkMode: defaultDarkMode(),
};

class DictationEditor extends React.Component<DictationEditorProps, DictationEditorState> {
  state: DictationEditorState;
  editorRef: React.RefObject<Editor>;
  editorContainerRef: React.RefObject<HTMLDivElement>;
  playerRef: React.RefObject<HTMLAudioElement>;
  speechRef: React.MutableRefObject<StreamingSpeechHandler | null>;
  stickyContextRef: React.RefObject<HTMLDivElement>;
  client: TranscriptServiceClient;
  keyboardUtils: KeyboardUtils;
  inputSignalStrengthRef: React.MutableRefObject<number>;
  isStreamInitializing: boolean = false;

  constructor(props: DictationEditorProps) {
    super(props);

    this.editorRef = React.createRef();
    this.editorContainerRef = React.createRef();
    this.playerRef = React.createRef();
    this.speechRef = React.createRef();
    this.stickyContextRef = React.createRef();
    this.client = new TranscriptServiceClient(props.transcriptServerUrl);
    this.keyboardUtils = new KeyboardUtils({
      onDownload: async () => {
        // If called via a shortcut we assume this is LSH and also stop to make sure results are finalized
        if (!this.state.recordingStopped) {
          await this.onStop();
        }

        this.onDownload();
      },
      onPlayback: this.onPlayback,
      onRecord: this.onRecord,
      onRestart: this.onRestart,
      onSave: this.onSave,
      onSeek: this.onSeek,
    });
    this.inputSignalStrengthRef = { current: 0 };

    const decorator = new CompositeDecorator([
      {
        strategy: syncWordStrategy,
        component: SyncWord,
        props: {
          getCurrentTime: () => {
            if (this.playerRef.current) return this.playerRef.current.currentTime;
            else return 0;
          },
          playerRef: this.playerRef,
        },
      },
    ]);

    this.state = {
      editorState: EditorState.createEmpty(decorator),
      ...initialDictationEditorState,
      languageCode: (props.languageCode ?? defaultLanguageCode) as LanguageCodeOption['value'],
      metadata: {
        ...initialDictationEditorState.metadata,
        subject: props.initialSubject ?? '',
        languageCode: props.languageCode ?? defaultLanguageCode,
        keywordsList: props.initialKeywords ?? [],
      },
    };
  }

  componentWillUnmount() {
    if (this.speechRef.current && !this.speechRef.current.closed) {
      this.speechRef.current.stop();
    }
    this.keyboardUtils.deregisterShortcutListeners();
  }

  setError = (error: string) => {
    if (isTransientGrpcError(error)) {
      console.warn('Suppressing transient gRPC error:', error);
      return;
    }
    this.setState({ error });
  };

  dismissError = () => {
    this.setState({ error: undefined });
  };

  componentDidMount() {
    if (this.state.metadata.subject) {
      this.setState({ waiting: true });
      const req = new ListTranscriptsRequest();
      req.setFilter(`metadata.subject CONTAINS "${this.state.metadata.subject}" `);
      this.client.listTranscripts(
        req,
        new grpc.Metadata({ Authorization: `Bearer ${this.props.accessToken}` }),
        (error, responseMessage) => {
          if (error) {
            console.warn('Ignoring server error:', error);
            this.setState({ waiting: false });
            return;
          }

          if (!responseMessage) {
            console.warn('No message in response, ignoring...');
            this.setState({ waiting: false });
            return;
          }

          if (responseMessage.getTranscriptsList().length === 0) {
            this.setState({ waiting: false });
            return;
          }

          const req = new GetTranscriptRequest();
          const transcriptName = responseMessage.getTranscriptsList()[0].getName();
          req.setName(transcriptName);
          this.client.getTranscript(
            req,
            new grpc.Metadata({ Authorization: `Bearer ${this.props.accessToken}` }),
            (error, transcript) => {
              if (error) {
                console.warn('Ignoring server error:', error);
                return;
              }

              if (!transcript) {
                console.warn('No message in response, ignoring...');
                return;
              }

              const words: WordInfo[] = [];
              transcript.getSegmentsList().forEach((segment) => {
                segment.getWordsList().forEach((word) => {
                  const startTime = durationToFloat(word.getStartTime());
                  const endTime = durationToFloat(word.getEndTime());
                  words.push({
                    startTime,
                    endTime,
                    word: word.getWord(),
                    id: `w_${startTime}_${endTime}`,
                  });
                });
              });

              if (words.length === 0) {
                console.warn(`Last transcript with subject ${this.state.metadata.subject} is empty.`);
                return;
              }

              if (this.playerRef.current) {
                this.onAudioSrcUpdated(transcript.getUri());
              }

              this.setState((prevState) => {
                const editorState = prevState.editorState;
                const contentState = editorState.getCurrentContent();
                const newContentState = this.addSegment(contentState, editorState.getSelection(), words);

                return {
                  editorState: EditorState.forceSelection(
                    EditorState.push(editorState, newContentState, 'insert-characters'),
                    newContentState.getSelectionAfter(),
                  ),
                  currentHypothesis: '',
                  transcriptName,
                  recordingStarted: true,
                  recordingStopped: true,
                  waiting: false,
                };
              });
            },
          );
        },
      );
    } else {
      // Perform an auth check by getting some imaginary transcript. This isn't optimal, but we want to catch auth
      // errors for query tokens early.
      const req = new GetTranscriptRequest();
      req.setName('check');
      this.client.getTranscript(
        req,
        new grpc.Metadata({ Authorization: `Bearer ${this.props.accessToken}` }),
        (error) => {
          if (error) {
            if (error.code == grpc.Code.Unauthenticated) {
              console.debug('not authenticated');
              this.setState({ waiting: false });
              this.props.signOut && this.props.signOut();
              return;
            } else if (error.code !== grpc.Code.NotFound) {
              console.error('Unknown error');
              this.setState({ waiting: false });
              this.setError(`Óþekkt villa: ${error.message}`);
            }
          }
        },
      );
    }
    this.keyboardUtils.registerShortcutListeners();
  }

  onMessage = (res: StreamingRecognizeResponse) => {
    if (res.getResultsList().length > 0) {
      const result = res.getResultsList()[0];
      if (!result.getIsFinal()) {
        const currentHypothesis = result.getAlternativesList()[0].getTranscript();

        this.setState({ currentHypothesis });
      } else {
        const wordInfos: WordInfo[] = res
          .getResultsList()[0]
          .getAlternativesList()[0]
          .getWordsList()
          .filter((wordInfo) => wordInfo.getWord().length > 0)
          .map((wordInfo) => {
            const startTime = durationToFloat(wordInfo.getStartTime());
            const endTime = durationToFloat(wordInfo.getEndTime());
            return {
              word: wordInfo.getWord() + (wordInfo.getWord().endsWith('\n') ? '' : ' '),
              startTime,
              endTime,
              id: `w_${startTime}_${endTime}`,
            };
          });

        const originalTranscript = this.state.originalTranscript;
        const segment = originalTranscript.addSegments();
        const wordList = res.getResultsList()[0].getAlternativesList()[0].getWordsList();
        wordList.forEach((word) => {
          const tWord = new Transcript.TranscriptSegment.WordInfo();
          tWord.setWord(word.getWord() + ' ');
          tWord.setEndTime(word.getEndTime());
          tWord.setStartTime(word.getStartTime());
          segment.addWords(tWord);
        });
        segment.setEndTime(wordList[wordList.length - 1].getEndTime());
        segment.setStartTime(wordList[0].getStartTime());

        this.setState({ originalTranscript });

        if (wordInfos.length < 1) return;

        this.setState((prevState) => {
          const editorState = prevState.editorState;
          const contentState = editorState.getCurrentContent();
          const newContentState = this.addSegment(contentState, editorState.getSelection(), wordInfos);

          this._hackyScrollToCaret();

          return {
            editorState: EditorState.forceSelection(
              EditorState.push(editorState, newContentState, 'insert-characters'),
              newContentState.getSelectionAfter(),
            ),
            currentHypothesis: '',
          };
        });
      }
    }
  };

  onAudioSrcUpdated = (audioURL: string) => {
    if (this.playerRef.current === null) {
      console.warn('no player');
      return;
    }
    const player = this.playerRef.current;
    const currentTime = this.state.currentTime;
    player.src = audioURL;
    player.currentTime = currentTime;
  };

  startStream = async () => {
    if (this.isStreamInitializing || this.speechRef.current) return;
    this.isStreamInitializing = true;

    try {
      // Obtaining the device labels only works if we already have recording
      // permission, and we have to include the label/deviceID when we request
      // the permission...
      const devices = await navigator.mediaDevices.enumerateDevices();
      const speechmike: MediaDeviceInfo | undefined = devices.find((device) => {
        return device.kind === 'audioinput' && device.label.toLowerCase().includes('speechmike');
      });
      const constraints = {
        audio: speechmike ? { deviceId: speechmike.deviceId } : true,
        video: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (stream) {
        // Double check in case closed while waiting
        if (!this.isStreamInitializing) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        this.speechRef.current = new StreamingSpeechHandler({
          stream,
          recVolume: this.state.currentRecVolume / 100,
          languageCode: this.state.languageCode,
          onMessage: this.onMessage,
          onEnd: (status: grpc.Code) => {
            if (status !== grpc.Code.OK) {
              this.setState({ recording: false });
              this.setError('Tenging við þjón mistókst. Prófaðu að endurglæða síðuna og reyna aftur.');
            }
          },
          onAudioSrcUpdated: this.onAudioSrcUpdated,
          onInput: this.onMicInput,
          serverUrl: this.props.speechServerUrl,
          serverKey: this.props.speechServerKey,
        });
      }
    } catch (e) {
      throw e;
    } finally {
      this.isStreamInitializing = false;
    }
  };

  onRecord = () => {
    const { recording, recordingStarted, recordingStopped } = this.state;
    if (recordingStarted && recordingStopped) {
      // Recording finished, user must create new one to continue recording.
      return;
    } else if (!recordingStarted && !recordingStopped) {
      this.onStart();
    } else if (recording) {
      this.onPause();
    } else {
      if (this.playerRef.current) {
        this.playerRef.current.pause();
      }
      this.onResume();
    }
  };

  onStart = () => {
    this.setState({ originalTranscript: new Transcript(), recordingStarted: true });
    this.startStream()
      .then(() =>
        this.setState({
          recording: true,
          error: undefined,
        }),
      )
      .catch((error) => {
        console.error(error);
        let userErrorMessage = 'Gat ekki hafið upptöku. Prófaðu að endurglæða síðuna og reyna aftur.';
        if (error instanceof DOMException) {
          switch (error.name) {
            case 'NotReadableError':
              userErrorMessage =
                'Gat ekki lesið upptökutæki vegna vélbúnaðarvillu. Athugaðu hvort hljóðnemi/diktafónn sé í lagi og reyndu aftur.';
              break;
            case 'NotFoundError':
              userErrorMessage =
                'Fann ekkert upptökutæki. Athugaðu hvort hljóðnemi/diktafónn sé tengdur og reyndu aftur.';
              break;
            case 'NotAllowedError':
              userErrorMessage = 'Leyfi til upptöku í vafra vantar. Athugaðu stillingar fyrir síðuna og reyndu aftur.';
              break;
          }
        }
        this.setState({
          recording: false,
          recordingStarted: false,
        });
        this.setError(userErrorMessage);
      });
  };

  onPause = () => {
    if (!this.speechRef.current) throw new Error('tiro/no-speech');
    const speech = this.speechRef.current;
    speech.pause();
    this.setState({
      recording: false,
    });
  };

  onSeek = (back?: boolean) => {
    const { recording, recordingStarted } = this.state;
    if (!recording && recordingStarted && this.playerRef.current) {
      const current = this.playerRef.current.currentTime;
      const seek = Math.max(0, current + (back ? -2 : 2));
      this.playerRef.current.currentTime = seek;
      this.setState({ currentTime: seek });
    }
  };

  onPlayback = () => {
    if (!this.state.recording && this.state.recordingStarted && this.playerRef.current) {
      if (this.playerRef.current.paused) {
        this.playerRef.current.play();
      } else {
        this.playerRef.current.pause();
      }
    }
  };

  onResume = () => {
    if (!this.speechRef.current) throw new Error('tiro/no-speech');
    const speech = this.speechRef.current;
    speech.resume();
    this.setState({
      recording: true,
    });
  };

  onRecordingVolumeChange = (volume: number) => {
    this.setState({ currentRecVolume: volume }, () => {
      setDefaultRecVolume(volume);
      if (!this.speechRef.current) return;

      const speech = this.speechRef.current;
      speech.setRecordingVolume(volume / 100);
    });
  };

  onMicInput = (signalStrength: number) => {
    this.inputSignalStrengthRef.current = signalStrength;
  };

  onToggleDarkMode = () => {
    this.setState(
      (prevState) => ({ darkMode: !prevState.darkMode }),
      () => setDefaultDarkMode(this.state.darkMode),
    );
  };

  _maybeUploadAudio = async (transcriptName: string): Promise<void> => {
    // TODO(rkjaran): Figure a clearer way of checking this
    if (this.state.transcriptName) {
      console.debug('Editing transcript... not uploading audio.');
      return;
    }

    if (!this.speechRef.current) {
      throw new Error("No player available, can't upload audio.");
    }

    const chunks: Uint8Array[] = this.speechRef.current.chunks.map((val) => new Uint8Array(val));

    const dataLength: number = chunks.reduce((total, chunk) => total + chunk.length, 0);
    const chunksCombined: Uint8Array = new Uint8Array(dataLength);

    let pos: number = 0;
    for (const chunk of chunks) {
      chunksCombined.set(chunk, pos);
      pos += chunk.length;
    }

    const header: ArrayBuffer = makeWAVHeader(chunksCombined.byteLength / 2, {
      sampleRate: this.speechRef.current.audioContext.sampleRate,
    });

    const chunksToUpload: ArrayBuffer = new Uint8Array([...new Uint8Array(header), ...chunksCombined]).buffer;
    await this._upload(chunksToUpload, transcriptName);
  };

  _upload = (chunksToWrite: ArrayBuffer, transcriptName: string): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      const initUploadReq: InitUploadRequest = new InitUploadRequest();
      initUploadReq.setResourceName(transcriptName);
      this.client.initUpload(
        initUploadReq,
        new grpc.Metadata({ Authorization: `Bearer ${this.props.accessToken}` }),
        (error: ServiceError | null, response: InitUploadResponse | null) => {
          if (error || !response) {
            reject(error);
            return;
          }

          this.setState({ uploadInProgress: true }, async () => {
            const gcsSignedUrl: string = response!.getGcsSignedUrl();
            const maxChunkSize = 2 * 1024 * 1024;
            let offset: number = 0;
            const errMsg: string = 'Villa kom upp við upphal!';

            while (this.state.uploadInProgress) {
              const chunkSize =
                chunksToWrite.byteLength - offset < maxChunkSize ? chunksToWrite.byteLength - offset : maxChunkSize;
              const rangeUpper: number = offset + chunkSize;

              try {
                const response = await axios.put(gcsSignedUrl, chunksToWrite.slice(offset, rangeUpper), {
                  headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Range': `bytes ${offset}-${rangeUpper - 1}/${chunksToWrite.byteLength}`,
                  },
                  validateStatus: (status) => status >= 200 && status < 400,
                });

                if (response.status === 308) {
                  // Chunk successfully received.
                  offset = parseInt(response.headers['range'].split('-')[1], 10);
                } else if (response.status === 200 || response.status === 201) {
                  console.info('Upload finished');
                  this.setState({ uploadInProgress: false }, () => resolve());
                } else if (response.status >= 500 && response.status <= 599) {
                  console.error(`Upload failed with a 5xx status code: ${response.status}`);
                  this.setState({ uploadInProgress: false }, () => {
                    this.setError(errMsg);
                    reject();
                  });
                }

                console.log(`Progress: ${(100 * (offset / chunksToWrite.byteLength)).toPrecision(4)}%`);
              } catch (error) {
                console.error(`Error while uploading file: ${error}`);
                this.setState({ uploadInProgress: false }, () => {
                  this.setError(errMsg);
                  reject();
                });
              }
            }
          });
        },
      );
    });

  // Create an empty transcript and return its name
  _maybeInitTranscript = (): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      // TODO(rkjaran): Support updating metadata fields. This is not actually needed for the only current use of
      //   this, so leave it for later.
      if (this.state.transcriptName) {
        resolve(this.state.transcriptName);
        return;
      }

      const md = new TranscriptMetadata();
      const subject = !this.state.metadata.subject ? 'Ónefnt diktat' : this.state.metadata.subject;
      md.setSubject(subject);
      md.setDictation(true);
      md.setFileType(this.state.metadata.fileType);
      md.setDescription(this.state.metadata.description);
      md.setLanguageCode(this.state.metadata.languageCode);
      md.setRecordingDuration(floatToDuration(this.state.currentTime));
      md.setKeywordsList(this.state.metadata.keywordsList);

      const transcript = this.state.originalTranscript;
      transcript.setMetadata(md);

      const req = new CreateTranscriptRequest();
      req.setTranscript(transcript);

      this.client.createTranscript(
        req,
        new grpc.Metadata({ Authorization: `Bearer ${this.props.accessToken}` }),
        (error: ServiceError | null, msg: CreateTranscriptResponse | null) => {
          if (error) {
            // TODO(rkjaran): handle
            console.error('upl error creating transcript', error);
            reject(error);
          } else if (msg) {
            console.debug(`upl successfully created new transcript '${msg.getName()}'`);
            resolve(msg.getName());
          }
        },
      );
    });

  _updateTranscript = (transcriptName: string, editorState: EditorState): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      // TODO(rkjaran): Everything currently converted to one large segment. We
      //                should store segment boundaries within `editorState`.
      const transcript = new Transcript();
      transcript.setName(transcriptName);

      const segment = convertToTranscriptSegmentProto(editorState.getCurrentContent());
      transcript.addSegments(segment);

      const req = new UpdateTranscriptRequest();
      req.setTranscript(transcript);

      this.client.updateTranscript(
        req,
        new grpc.Metadata({ Authorization: `Bearer ${this.props.accessToken}` }),
        (error: ServiceError | null, msg: Transcript | null) => {
          if (error) {
            console.error('upl error updating transcript:', error);
            reject(error);
          } else if (msg) {
            console.debug(`Successfully updated transcript '${msg.getName()}'`);
            resolve();
          }
        },
      );
    });

  onStop = (restart?: boolean) =>
    new Promise<void>((resolve) => {
      // If we are still initializing, cancel the initialization
      if (this.isStreamInitializing) {
        this.isStreamInitializing = false;
      }

      if (!this.speechRef.current) return;
      const speech = this.speechRef.current;

      if (!speech.closed) {
        speech.close();
      }

      const self = this;
      this.speechRef.current.addEventListener('end', function end() {
        if (self.speechRef.current) {
          self.speechRef.current.removeEventListener('end', end);
        }

        restart = typeof restart === 'boolean' && restart;

        self.setState({
          recording: false,
          recordingStopped: restart ? false : true,
        });

        resolve();
      });
    });

  onSave = async () => {
    if (this.state.recordingStarted) {
      if (this.state.recording) {
        this.onPause();
      }

      if (this.playerRef.current && !this.playerRef.current.paused) {
        this.playerRef.current.pause();
      }
      if (!this.state.recordingStopped) {
        await this.onStop();
      }

      await this.save();

      if (this.props.onPostSave) {
        this.props.onPostSave(this.state.transcriptName);
      }
    }
  };

  save = async () => {
    this.setState({ saving: true });
    try {
      if (!this.playerRef.current) throw Error('tiro/no-player');
      const transcriptName: string = await this._maybeInitTranscript();
      await this._maybeUploadAudio(transcriptName);
      await this._updateTranscript(transcriptName, this.state.editorState);
      this.setState({
        saved: true,
        saving: false,
      });
    } catch (error) {
      this.setState({ saving: false });
      this.setError(`Tókst ekki að vista (${error})`);
    }
  };

  onReturn = (_: KeyboardEvent, editorState: EditorState): DraftHandleValue => {
    this.setState({
      editorState: EditorState.push(
        editorState,
        Modifier.insertText(editorState.getCurrentContent(), editorState.getSelection(), '\n'),
        'insert-characters',
      ),
    });
    return 'handled';
  };

  handleKeyCommand = (command: TranscriptEditorCommand, editorState: EditorState): DraftHandleValue => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getStartKey());
    const entityKey = block.getEntityAt(selection.getStartOffset());
    switch (command) {
      case 'transcript-editor-pause-or-play': {
        if (this.state.recording) return 'handled';
        if (entityKey) {
          const syncWordEntity = content.getEntity(entityKey);
          const wordInfo: WordInfo = syncWordEntity.getData();

          if (this.playerRef.current) {
            this.playerRef.current.currentTime = wordInfo.startTime;
          }
        }
        return 'handled';
      }
      case 'transcript-editor-move-back': /* fallthrough */
      case 'transcript-editor-move-forward': {
        const syncWordEntityChar = block
          .getCharacterList()
          .slice(selection.getStartOffset())
          .find((char) => {
            if (!char) return false;
            const charEntityKey = char.getEntity();
            if (!charEntityKey) return false;
            const entity = content.getEntity(charEntityKey);
            return entity.getType() === EntityType.SYNC_WORD && charEntityKey !== entityKey;
          });
        if (!syncWordEntityChar) return 'handled';
        const syncWordEntityKey = syncWordEntityChar.getEntity();
        if (!syncWordEntityKey) return 'handled';
        const syncWordEntity = content.getEntity(syncWordEntityKey);
        const wordInfo: WordInfo = syncWordEntity.getData();
        if (this.playerRef.current) {
          this.playerRef.current.currentTime = wordInfo.startTime;
        }
        return 'handled';
      }
      default:
        return 'not-handled';
    }
  };

  onChange = (editorState: EditorState) => {
    this.setState({ editorState });
  };

  // Hacky dom manipulation to scroll to caret
  _hackyScrollToCaret = debounce(() => {
    if (!this.editorRef.current) return;

    const range = document.createRange();
    const selection = window.getSelection();

    if (!selection || !selection.focusNode) return;

    range.setStart(selection.focusNode, selection.focusOffset);

    const fakeCaret = document.createElement('span');
    fakeCaret.style.visibility = 'hidden';
    fakeCaret.textContent = '\u200B';

    range.insertNode(fakeCaret);

    const scrollRect = this.editorContainerRef.current?.getBoundingClientRect();
    const rect = fakeCaret.getBoundingClientRect();

    if (scrollRect && (rect.top < scrollRect.top || rect.bottom > scrollRect.bottom)) {
      fakeCaret.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }

    const parent = fakeCaret.parentElement;
    fakeCaret.remove();

    // Draft.js assumes a span contains only a single TEXT node, but inserting and then removing the span leaves
    // behind two TEXT nodes before and after where the span was inserted. Therefore, we have to normalize the parent
    // element to merge the remaining TEXT nodes.
    parent?.normalize();
  }, 100);

  handleBeforeInput = (
    chars: string,
    editorState: EditorState,
    /* eventTimeStamp */ _: number,
  ): Draft.DraftHandleValue => {
    // If we're at the beginning look forward for a SYNC_WORD entity and merge
    // Otherwise look backwards for a SYNC_WORD
    // If there's no SYNC_WORD create one with a startTime = endTime = 0
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getStartKey());

    const insertContent = (newContent: ContentState) => {
      this.setState({
        editorState: EditorState.push(editorState, newContent, 'insert-characters'),
      });
      this._hackyScrollToCaret();
    };

    // Look backward
    const backChar = block
      .getCharacterList()
      .slice(0, selection.getStartOffset())
      .reverse()
      .find((char) => {
        if (!char) return false;
        const charEntityKey = char.getEntity();
        if (!charEntityKey) return false;
        const entity = content.getEntity(charEntityKey);
        return entity.getType() === EntityType.SYNC_WORD;
      });

    if (backChar) {
      const newContent = Modifier.insertText(content, selection, chars, undefined, backChar.getEntity());
      insertContent(newContent);
      return 'handled';
    }

    // Look forward
    const forwardChar = block
      .getCharacterList()
      .slice(selection.getStartOffset())
      .find((char) => {
        if (!char) return false;
        const entityKey = char.getEntity();
        if (!entityKey) return false;
        const entity = content.getEntity(entityKey);
        return entity.getType() === EntityType.SYNC_WORD;
      });

    if (forwardChar) {
      const newContent = Modifier.insertText(content, selection, chars, undefined, forwardChar.getEntity());
      insertContent(newContent);
      return 'handled';
    }

    // No SYNC_WORD found, create a new one
    const contentWithKey = content.createEntity(EntityType.SYNC_WORD, 'MUTABLE', {
      word: chars,
      startTime: 0,
      endTime: 0,
    } as WordInfo);

    const newContent = Modifier.insertText(
      contentWithKey,
      selection,
      chars,
      undefined,
      contentWithKey.getLastCreatedEntityKey(),
    );

    insertContent(newContent);
    return 'handled';
  };

  // Add wordInfos from transcript segment to `initialSelection` of `initialContentState`
  addSegment = (
    initialContentState: ContentState,
    initialSelection: SelectionState,
    wordInfos: WordInfo[],
  ): ContentState => {
    // Boundary-aware post-processing (spacing, capitalization, romanisation). Each step reads the
    // running { contentState, selection, wordInfos } and updates one part; add or reorder steps in
    // src/dictate/postprocess.ts.
    const {
      contentState,
      selection,
      wordInfos: processedWordInfos,
    } = runSegmentPostProcess({
      contentState: initialContentState,
      selection: initialSelection,
      wordInfos,
    });

    const contentStateWithWords = processedWordInfos.reduce(
      (acc: { cs: ContentState; ss: SelectionState }, wordInfo: WordInfo) => {
        const contentStateWithEntity = acc.cs.createEntity(EntityType.SYNC_WORD, 'MUTABLE', wordInfo);
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const contentStateWithWord = Modifier.replaceText(
          contentStateWithEntity,
          acc.ss,
          wordInfo.word,
          undefined,
          entityKey,
        );
        return { cs: contentStateWithWord, ss: contentStateWithWord.getSelectionAfter() };
      },
      { cs: contentState, ss: selection },
    ).cs;

    return contentStateWithWords;
  };

  onTimeUpdate = (currentTime: number) => {
    this.setState({ currentTime });
  };

  onDurationChange = (duration: number) => {
    this.setState({ duration });
  };

  onDownload = () => {
    const textContent = this.state.editorState.getCurrentContent().getPlainText('');
    const textarea = document.createElement('textarea');
    textarea.value = textContent;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  onRestart = () => {
    this.isStreamInitializing = false;
    if (this.state.recording) this.onPause();
    // We don't need to wait for the promise here since we force cleanup below
    if (this.speechRef.current) {
      if (!this.speechRef.current.closed) {
        this.speechRef.current.close();
      }
      this.speechRef.current = null;
    }
    if (this.playerRef.current) this.playerRef.current.src = '';
    this.setState((prevState) => {
      return {
        editorState: EditorState.createEmpty(prevState.editorState.getDecorator()),
        ...initialDictationEditorState,
        languageCode: this.state.languageCode,
        darkMode: this.state.darkMode,
        metadata: {
          ...initialDictationEditorState.metadata,
          languageCode: this.state.languageCode,
        },
      };
    });
  };

  get isRestricted() {
    return !!this.props.restricted;
  }

  render() {
    const { darkMode } = this.state;
    return (
      <ConfigProvider theme={{ algorithm: darkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
        <div
          className={`dictation-editor${darkMode ? ' dictation-editor--dark' : ''}`}
          style={{ height: '100%', position: 'relative' }}
        >
          {this.state.error ? (
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                margin: 'auto',
                width: 'fit-content',
                height: 'fit-content',
                zIndex: 100,
              }}
            >
              <ErrorMessage
                header='Villa!'
                message={this.state.error}
                onDismiss={() => this.setState({ error: undefined, recordingStarted: false })}
              />
            </div>
          ) : null}
          <div style={{ height: '100%' }}>
            <Row>
              <Col span={24}>
                <Row>
                  <Col span={13}>
                    <RecordingControls
                      onStart={this.onStart}
                      onStop={this.onStop}
                      onPause={this.onPause}
                      onResume={this.onResume}
                      onSave={this.onSave}
                      onDownload={this.onDownload}
                      onRestart={this.onRestart}
                      onRecordingVolumeChange={this.onRecordingVolumeChange}
                      darkMode={this.state.darkMode}
                      onToggleDarkMode={this.onToggleDarkMode}
                      currentRecVolume={this.state.currentRecVolume}
                      inputSignalStrengthRef={this.inputSignalStrengthRef}
                      recording={this.state.recording}
                      started={this.state.recordingStarted}
                      stopped={this.state.recordingStopped}
                      saved={this.state.saved}
                      saving={this.state.saving}
                      restricted={this.isRestricted}
                    />
                  </Col>
                  <Col span={11}>
                    <AudioPlayer
                      disabled={this.state.recording || !this.state.recordingStarted}
                      playerRef={this.playerRef}
                      onTimeUpdate={this.onTimeUpdate}
                      onDurationChange={this.onDurationChange}
                      defaultTime={0}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ height: 'calc(100% - 100px)', width: '100%' }}>
              <Col span={24} style={{ height: '100%' }}>
                <Card
                  ref={this.editorContainerRef}
                  className='transcript-segment'
                  onClick={() => this.editorRef.current && this.editorRef.current.focus()}
                  color='blue'
                  style={{ height: '100%' }}
                  loading={this.state.waiting}
                >
                  <CurrentTimeContext.Provider value={this.state.currentTime}>
                    <Editor
                      ref={this.editorRef}
                      readOnly={!this.state.editable}
                      editorState={this.state.editorState}
                      onChange={this.onChange}
                      handleBeforeInput={this.handleBeforeInput}
                      handleReturn={(_, editorState) => this.handleBeforeInput('\n', editorState, 0)}
                      handleDrop={() => 'handled'}
                      keyBindingFn={this.keyboardUtils.draftKeyBindingFn}
                      handleKeyCommand={this.handleKeyCommand}
                      stripPastedStyles
                      placeholder='Texti birtist hér...'
                    />
                  </CurrentTimeContext.Provider>
                  <span className='editor-hypothesis'>{this.state.currentHypothesis}</span>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </ConfigProvider>
    );
  }
}

export default DictationEditor;
